import { useState } from 'react';
import { useI18n } from '@/client/i18n/I18nContext';
import { gaEvent } from '@/client/analytics/ga';
import { localizedPath } from '@/lib/seo';
import type { Locale } from '@/types';

/** Share links for an article URL. Plain links: no third-party scripts or pixels. */
export function shareLinks(url: string, title: string) {
  const u = encodeURIComponent(url);
  return {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${u}`,
  };
}

/**
 * Discreet «Compartir» row at the end of an article: WhatsApp · Facebook · X ·
 * Copiar enlace, plus the phone's native share sheet when the browser has one
 * (that is how Instagram is reached: it has no web share link). Counts a GA4
 * `share` event (consent-gated in gaEvent).
 */
export function ArticleShare({ slug, title, locale }: { slug: string; title: string; locale: Locale }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${localizedPath(locale, `/the-edit/post/${slug}`)}`;
  const links = shareLinks(url, title);
  const track = (method: string) => gaEvent('share', { method, content_type: 'article', item_id: slug });
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      track('copy_link');
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked (in-app browsers such as Instagram's, insecure
      // context): show the link so it can be copied by hand.
      window.prompt(t('share.copy'), url);
    }
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title, url });
      track('native');
    } catch {
      /* dismissed */
    }
  };

  return (
    <div className="article-share" role="group" aria-label={t('share.label')}>
      <span className="article-share__label">{t('share.label')}</span>
      <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp')}>WhatsApp</a>
      <a href={links.facebook} target="_blank" rel="noopener noreferrer" onClick={() => track('facebook')}>Facebook</a>
      <a href={links.x} target="_blank" rel="noopener noreferrer" onClick={() => track('x')}>X</a>
      <button type="button" onClick={copy} aria-live="polite">
        {copied ? t('share.copied') : t('share.copy')}
      </button>
      {canNativeShare ? (
        <button type="button" onClick={nativeShare}>
          {t('share.more')}
        </button>
      ) : null}
    </div>
  );
}
